// Select Studio field — text input with portaled location dropdown
// before a location is selected, "<Location name> · Change*" pill after.
//
// Used in two places:
//   1. LeadCapture (consolidated step 1: Name + Email + Select Studio)
//   2. BookingStripPanel's top strip (post-lead-capture, where the user
//      can still swap locations to drive different availability)
//
// The dropdown options render via AnchoredPortal so they aren't clipped
// by the modal's overflow:auto wrapper. Position tracks the input's
// bounding rect on scroll/resize.

import { useEffect, useRef, useState } from 'react';
import styles from '../styles/widget.module.css';
import { listLocations, geocodePlaceName } from '../api/client';
import { geocodeMetro, geocodeZip, looksLikePostcode } from '../utils/geocode';
import { haversineMiles } from '../utils/distance';
import { useClickOutside } from '../hooks/useClickOutside';
import { AnchoredPortal } from './AnchoredPortal';
import { countryConfigOrDefault } from '../config/countries';
import { storeDisplayName, matchesStoreName } from '../data/storeDisplayNames';
import type { Location } from '../types';
import type { BookingAction, BookingState } from '../state/machine';

const DEFAULT_MAX_RADIUS_MILES = 100;

// Country-scoped max radius is passed in km on non-US configs; convert here.
// 1 mile = 1.609344 km, so km * 0.621371 = miles.
function toMiles(value: number, unit: 'mi' | 'km'): number {
  return unit === 'mi' ? value : value * 0.621371;
}

export interface RankedLocation extends Location {
  distanceMiles?: number;
}

/**
 * Append the studios a search did not match, so the list is never empty.
 *
 * In markets where a few studios cover a whole country, most searches match
 * nothing on distance — and an empty dropdown reads as "no studios here"
 * rather than "none nearby". Matches keep their ranking at the top; the rest
 * follow alphabetically so the customer can still see, and choose, the studio
 * that is a drive away.
 */
function withRemainder(
  matches: RankedLocation[],
  all: Location[],
  showAll: boolean,
): RankedLocation[] {
  if (!showAll) return matches;
  const seen = new Set(matches.map((m) => m.id));
  const rest = all
    .filter((l) => !seen.has(l.id))
    .sort((a, b) => a.name.localeCompare(b.name));
  return [...matches, ...rest];
}

export async function rankLocations(
  query: string,
  all: Location[],
  maxRadiusMiles: number = DEFAULT_MAX_RADIUS_MILES,
  country: string | null | undefined = 'us',
): Promise<RankedLocation[]> {
  const showAll = countryConfigOrDefault(country).showAllStudios;
  const trimmed = query.trim();
  // Below the typing threshold the market's whole list is the useful answer,
  // rather than nothing at all.
  if (trimmed.length < 3) {
    return showAll ? withRemainder([], all, true) : [];
  }

  // Three ways to reach coordinates, cheapest first:
  //
  //   postcode  → zippopotam, keyless and callable straight from the browser
  //   metro     → the local table, no network at all
  //   anything  → the middleware, which proxies Mapbox
  //
  // The metro table stays in front of the network call deliberately. It covers
  // the handful of city names customers type most, and this runs as the user
  // types — spending a round trip on "Sydney" when the answer is already in the
  // bundle would make the common case slower to fix the rare one.
  //
  // The middleware leg is what CC's Australian team asked for: postcode search
  // worked, and any city other than the store's own returned nothing, because
  // the table has six entries per market and Parramatta is not one of them.
  const geo = looksLikePostcode(trimmed, country)
    ? await geocodeZip(trimmed, country)
    : (geocodeMetro(trimmed, country) ?? (await geocodePlaceName(trimmed, country)));
  if (geo) {
    {
      // ~60% of stores have no geocoded lat/lng (Nominatim chokes on
      // Suite/Unit addresses). A pure distance rank silently drops them,
      // sending a customer in e.g. Albany NY (un-geocoded Albany store)
      // to Hartford CT just because Hartford has coords. Mitigate by
      // also including stores whose state/city matches the zip's place,
      // and tier the results so the customer's local store wins.
      const stateNeedle = geo.state.toLowerCase();
      const stateAbbrNeedle = geo.stateAbbr.toLowerCase();
      const cityNeedle = geo.city.toLowerCase();

      // A metro-name match resolves to coordinates only — it carries no city
      // or state. Those blanks must not be treated as needles: `includes('')`
      // is true of every string, which would mark every store "same city" and
      // flatten the distance ranking that makes the result useful.
      const isSameState = (loc: Location): boolean => {
        if (!stateNeedle && !stateAbbrNeedle) return false;
        const parts = (loc.address ?? '')
          .split(',')
          .map((p) => p.trim().toLowerCase());
        return parts.includes(stateNeedle) || parts.includes(stateAbbrNeedle);
      };
      const isSameCity = (loc: Location): boolean => {
        if (!cityNeedle) return false;
        const n = loc.name.toLowerCase();
        const addr = (loc.address ?? '').toLowerCase();
        return n === cityNeedle || n.includes(cityNeedle) || addr.includes(cityNeedle);
      };

      interface Entry {
        loc: Location;
        distanceMiles?: number;
        sameState: boolean;
        sameCity: boolean;
      }

      const entries: Entry[] = all.map((loc) => ({
        loc,
        distanceMiles:
          loc.lat !== undefined && loc.lng !== undefined
            ? haversineMiles(geo, { lat: loc.lat, lng: loc.lng })
            : undefined,
        sameState: isSameState(loc),
        sameCity: isSameCity(loc),
      }));

      const kept = entries.filter((e) => {
        if (e.sameCity) return true;
        if (e.sameState) return true;
        // A studio with no coordinates cannot be measured, and dropping it
        // makes it unbookable — which is exactly what happened to Edinburgh:
        // active in Salesforce, returned by the API, and invisible in the UK
        // picker because it has no lat/lng to compare. Not being able to tell
        // how far away a studio is is not a reason to pretend it does not
        // exist. It sorts last, since everything measurable is ranked above.
        if (e.distanceMiles === undefined) return true;
        return e.distanceMiles <= maxRadiusMiles;
      });

      kept.sort((a, b) => {
        // Tier 1: same-city always wins (customer's actual hometown store)
        if (a.sameCity !== b.sameCity) return a.sameCity ? -1 : 1;
        // Tier 2: distance ascending if both have it; coord stores ahead
        //         of coordless within the same tier
        if (a.distanceMiles !== undefined && b.distanceMiles !== undefined) {
          return a.distanceMiles - b.distanceMiles;
        }
        if (a.distanceMiles !== undefined) return -1;
        if (b.distanceMiles !== undefined) return 1;
        // Tier 3: same-state ahead of out-of-state
        if (a.sameState !== b.sameState) return a.sameState ? -1 : 1;
        // Tier 4: alphabetical
        return a.loc.name.localeCompare(b.loc.name);
      });

      if (kept.length > 0) {
        return withRemainder(
          kept.map((e) => ({ ...e.loc, distanceMiles: e.distanceMiles })),
          all,
          showAll,
        );
      }
      // Fall through to text match if the geocode found nothing in-state
      // and no coord stores nearby — let the user keep narrowing manually.
    }
  }

  const q = trimmed.toLowerCase();
  const textMatches = all
    .filter(
      (loc) =>
        `${loc.name} ${loc.address ?? ''}`.toLowerCase().includes(q) ||
        // "Sydney" is not in the Salesforce name — it exists only in the
        // display name, and it is what an Australian customer types.
        matchesStoreName(loc.name, country, q),
    )
    .sort((a, b) => a.name.localeCompare(b.name));
  return withRemainder(textMatches, all, showAll);
}

interface LocationFieldProps {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
  hideSelectedPill?: boolean;
  /** Lay the field out as a stacked label + input (default true, matches
   *  LeadCapture's three-column row). Set to false for the strip's
   *  horizontal pill flow. */
  stackedLabel?: boolean;
  /** Force the results dropdown placement. Default: 'bottom' on desktop,
   *  'top' on mobile (which pins above the on-screen keyboard — right for the
   *  fullscreen modal flow). Inline Step 1 renders in-flow in the page, so it
   *  passes 'bottom' to anchor the results directly beneath the field. */
  dropdownPlacement?: 'top' | 'bottom' | 'auto';
  /** When true, the (pre-selection) input shows the subtle red hairline
   *  required-field border. Driven by the parent's touched+invalid logic;
   *  only meaningful while no location is selected. */
  invalid?: boolean;
  /** Fired when the input blurs — lets the parent mark this field "touched"
   *  so its validation border can appear. */
  onBlur?: () => void;
  /** Render the field inert — input disabled, dropdown suppressed, Change
   *  hidden on the selected pill. Used by the reschedule flow, where the
   *  studio comes from the looked-up appointment rather than the user. */
  disabled?: boolean;
}

export function LocationField({
  state,
  dispatch,
  hideSelectedPill = false,
  stackedLabel = true,
  dropdownPlacement,
  invalid = false,
  onBlur,
  disabled = false,
}: LocationFieldProps) {
  const [allLocations, setAllLocations] = useState<Location[] | null>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(state.locationQuery);
  const [ranked, setRanked] = useState<RankedLocation[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Named from the market's config rather than an equality check against one
  // spelling of it. This used to read `postalCodeLabel === 'Postcode' ?
  // 'Postcode' : 'Zip'`, so the moment the UK moved to "Post Code" the
  // placeholder fell through to the US branch and the field silently read
  // "Enter Zip or City" in Britain — a failure that renders perfectly.
  // The US says "Zip" here but "Zip Code" on the label, hence the short form
  // being its own field rather than reusing the label.
  const postal = countryConfigOrDefault(state.country);
  const searchPlaceholder =
    `Enter ${postal.postalCodeShortLabel ?? postal.postalCodeLabel} or City`;

  const wrapperRef = useClickOutside<HTMLFormElement>(
    () => {
      setOpen(false);
    },
    {
      enabled: open,
      ignoreSelector: '[data-cc-dropdown-portal]',
    },
  );

  // Country-scoped: refetch when state.country changes so a UK embed doesn't
  // show the cached US locations (or vice versa). Setting allLocations back to
  // null triggers the fetch branch on the next render.
  useEffect(() => {
    setAllLocations(null);
    if (state.selectedLocation && state.selectedLocation.country && state.selectedLocation.country !== state.country) {
      // Previously-selected location is in a different country than the current
      // scope — clear it so the user picks fresh from the country-scoped list.
      dispatch({ type: 'SELECT_LOCATION', payload: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.country]);

  useEffect(() => {
    if (!allLocations) {
      listLocations(state.country)
        .then(setAllLocations)
        .catch(() => setAllLocations([]));
    }
  }, [allLocations, state.country]);

  // Country-aware max radius: config value is in native unit (mi for US/CA,
  // km for UK/AU); internal filter is miles-only, so convert.
  const countryConfig = countryConfigOrDefault(state.country);
  const maxRadiusMiles = toMiles(countryConfig.maxSearchRadius, countryConfig.distanceUnit);

  useEffect(() => {
    if (!allLocations) return;
    rankLocations(query, allLocations, maxRadiusMiles, state.country).then(setRanked);
  }, [query, allLocations, maxRadiusMiles, state.country]);

  // On mobile the filtered results render IN-FLOW directly beneath the input
  // (see below) rather than through the fixed-position body portal — position:
  // fixed fights the mobile keyboard/visual-viewport and landed the results far
  // from the field. Desktop keeps the portal (it escapes the modal's overflow
  // and works fine). Track the viewport so rotation flips between the two.
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 1024,
  );
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Shared results list — rendered either in-flow (mobile) or portaled (desktop).
  const renderResults = (extraClass = '') => (
    <div
      className={`${styles.locationDropdown} ${extraClass}`.trim()}
      data-cc-dropdown-portal="location"
    >
      {/* Describes the ordering, not a radius. This used to read "N Locations
          within 600 km", which was not true: the count is the whole ranked
          list, and ranking deliberately keeps studios that the distance filter
          would drop — most stores have no coordinates, so city and state
          matches are carried through too. Canada made it obvious, listing four
          studios "within 600 km" when only Calgary is; Toronto is 2,708km
          away. Stating the order is both accurate and the thing a customer
          actually needs to know. */}
      <div className={styles.locationDropdownHeader}>
        {ranked.length} {ranked.length === 1 ? 'Location' : 'Locations'}
        {ranked.length > 1 ? ', nearest first' : ''}
      </div>
      {ranked.slice(0, 6).map((loc) => (
        <div
          key={loc.id}
          className={styles.locationRow}
          onClick={() => {
            dispatch({ type: 'SELECT_LOCATION', payload: loc });
            dispatch({ type: 'SET_NEARBY_LOCATIONS', payload: ranked });
            setOpen(false);
          }}
        >
          <div className={styles.locationName}>
            {storeDisplayName(loc.name, state.country).toUpperCase()}
          </div>
          {loc.address && <div className={styles.locationAddress}>{loc.address}</div>}
          {loc.distanceMiles !== undefined && (
            <div className={styles.locationDistance}>
              {countryConfig.distanceUnit === 'km'
                ? `${Math.round(loc.distanceMiles / 0.621371)} km away`
                : `${Math.round(loc.distanceMiles)} miles away`}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  // PILL — when a location is selected
  if (state.selectedLocation) {
    if (hideSelectedPill) return null;
    if (stackedLabel) {
      return (
        <div className={styles.field}>
          <label className={styles.label}>Select Studio*</label>
          <div className={styles.locationPillStacked}>
            <span className={styles.locationPillName}>
              {storeDisplayName(state.selectedLocation.name, state.country)}
            </span>
            {!disabled && (
              <button
                type="button"
                className={styles.locationPillChange}
                onClick={() => {
                  setQuery('');
                  dispatch({ type: 'SELECT_LOCATION', payload: null });
                }}
              >
                Change
              </button>
            )}
          </div>
        </div>
      );
    }
    return (
      <div className={styles.stripPill}>
        <span className={styles.label}>Select Studio</span>
        <div className={styles.stripPillValue}>
          <span>{storeDisplayName(state.selectedLocation.name, state.country)}</span>
          {!disabled && (
            <button
              type="button"
              className={styles.stripPillChange}
              onClick={() => {
                setQuery('');
                dispatch({ type: 'SELECT_LOCATION', payload: null });
              }}
            >
              Change
            </button>
          )}
        </div>
      </div>
    );
  }

  // INPUT + DROPDOWN — pre-selection
  return (
    <form
      className={styles.field}
      ref={wrapperRef}
      method="post"
      action="javascript:void(0)"
      autoComplete="on"
      onSubmit={(e) => e.preventDefault()}
    >
      <label className={styles.label} htmlFor="cc-postal-code">
        Select Studio*
      </label>
      <input
        ref={inputRef}
        id="cc-postal-code"
        name="postal-code"
        className={styles.input}
        type="text"
        autoComplete="postal-code"
        autoCorrect="off"
        spellCheck={false}
        placeholder={searchPlaceholder}
        value={query}
        disabled={disabled}
        aria-invalid={invalid}
        onChange={(e) => {
          if (disabled) return;
          setQuery(e.target.value);
          dispatch({ type: 'SET_LOCATION_QUERY', payload: e.target.value });
          setOpen(true);
        }}
        onFocus={() => {
          if (disabled) return;
          setOpen(true);
        }}
        onBlur={() => onBlur?.()}
      />
      {isNarrow ? (
        // Mobile: results in-flow, directly beneath the input — always where
        // the user is looking, immune to the mobile fixed-position/keyboard bugs.
        !disabled && open && ranked.length > 0 && renderResults(styles.locationDropdownInline)
      ) : (
        // Desktop: portal so the dropdown floats over the modal's overflow.
        <AnchoredPortal
          anchorRef={inputRef}
          isOpen={!disabled && open && ranked.length > 0}
          placement={dropdownPlacement ?? 'bottom'}
        >
          {renderResults()}
        </AnchoredPortal>
      )}
    </form>
  );
}
